import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from '../ProductActivityPage.module.css'
import o from '../../components/Overlay.module.css'
import pv from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton } from '../../components/pending'
import { dayLabel } from './model'
import { itemName } from './itemName'

// Late data (D7.1, D7.1b, D7.2): what arrived after its day was shut. Nothing here is applied by itself. A person
// opens an item, sees what applying it would change (a dry run on the server, writing nothing), and applies it with
// a reason, or dismisses it with one. A locked day cannot be applied to: unlock it first, which recalculates the
// day. An "arrived after close" row is information: the cycle was recorded as normal; it can only be acknowledged.
// A "weighing conflict" row (D7.8 D) is a Crew weighing held because it would change a weight a bin carries or reaches a shut day.
// It is listed and counted here, but it is decided on the Batches screen (batches edit), where both readings are side by side.
// A "QC held for review" row (D7.3 C, D7.6) is a quality scan whose set did not check out: the engine kept it and applied
// nothing. Accepting it, one at a time and with a reason, lets it be read as any other scan; dismissing it leaves it held.
// A "Crew clock" row is a Dataphyll Crew clock-on or clock-off that reached a shut day (or no time zone placed it on
// one): nothing went on the timesheet. Applying it, one at a time, puts it there as a Live day would have; a clock-on
// brings its clock-off with it.

export type LateKind = 'staged_claim' | 'late_qc' | 'qc_review' | 'arrived_after_close' | 'other_site_tag' | 'weighing_conflict' | 'crew_clock'
export const LOCKED_TEXT = 'This day is locked. Unlock it first.'

export interface LateItem {
  kind: LateKind
  id: string
  item_code: string | null
  tagless?: boolean
  cycle_id?: string | null
  title: string
  summary: string
  note?: string | null
  worker?: string | null
  captured_at?: string | null
  arrived_at?: string | null
  work_date?: string | null
  day_state?: 'LIVE' | 'CLOSED' | 'LOCKED' | null
  locked_dates?: string[]
  actionable?: boolean
  // false: never applied in a batch (a QC review is a person's judgement, one scan at a time)
  bulk?: boolean
  review_hold?: string | null
  blocked?: { code: string; message: string; locked_dates: string[] } | null
  // weighing_conflict: where it is decided, and what was held
  resolve_via?: { permission: string; path: string }
  held?: { operator: string; net_kg: number }
  // crew_clock: a clock-on or a clock-off, and why it was held
  clock_kind?: 'clock_on' | 'clock_off'
  hold_reason?: string
  // resolved items
  resolution_id?: string
  decision?: 'applied' | 'dismissed'
  actor?: string
  reason?: string
  decided_at?: string
}

export interface LateCounts {
  staged_claim: number
  late_qc: number
  qc_review?: number
  arrived_after_close: number
  other_site_tag?: number
  weighing_conflict?: number
  crew_clock?: number
  total: number
}

interface CycleChange {
  id: string
  item_code: string
  tagless?: boolean
  is_new: boolean
  changes: { field: string; label: string; before: string; after: string }[]
  worker_change: { before: string; after: string } | null
  history_added: string[]
}
interface WageTarget {
  human_id: string
  worker: string
  work_date: string
  day_state: string
  queued: boolean
}
interface Preview {
  kind: LateKind
  id: string
  preview_hash?: string
  blocked?: { code: string; message: string; locked_dates: string[] } | null
  informational?: boolean
  resolved?: { actor: string; decided_at: string; decision: string }
  message?: string
  cycles?: CycleChange[]
  wage_targets?: WageTarget[]
  claim_changes?: unknown[]
  review?: { quality_name: string; qc_factor: number; review_hold: string; reason: string | null }
  // crew_clock: what applying it puts on the timesheet
  clock?: { status: string; reason: string | null; text: string }
}

const KIND_LABEL: Record<LateKind, string> = {
  staged_claim: 'Held weigh or claim',
  late_qc: 'Late quality scan',
  qc_review: 'QC held for review',
  arrived_after_close: 'Arrived after close',
  other_site_tag: 'Picker from another site',
  weighing_conflict: 'Weighing conflict',
  crew_clock: 'Crew clock',
}

const stamp = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}

// Why a scan was held, in the words a person needs before accepting it.
export const HOLD_TEXT: Record<string, string> = {
  set_unknown: 'The scan names a quality set that is not one of this workspace’s.',
  set_missing: 'The scan names no quality set, and its team has none to take one from.',
  set_differs: 'The scan’s quality set is not the one its team uses.',
  set_archived: 'The scan’s quality set was archived before the scan was captured.',
  quality_not_in_set: 'The scan’s quality is not in its quality set.',
  quality_archived: 'The scan’s quality was archived before the scan was captured.',
}

const dayTag = (s?: string | null) => (s === 'LOCKED' ? 'Locked day' : s === 'CLOSED' ? 'Closed day' : 'Live day')

interface PanelProps {
  user: { getIdToken: () => Promise<string> } | null
  // Told after a decision so the page refreshes its cycles and the badge.
  onChanged: () => void
  toast: (message: string) => void
  // product_activity edit: without it the list and the previews are read-only.
  canEdit: boolean
  // The list reloads when this changes (a push from the server, or the badge's own refresh).
  refreshKey?: number
  // Opens a held weighing where it is decided (Batches). Absent when the person cannot read Batches.
  onOpenConflict?: (id: string) => void
  // Opens a locked day on Timesheets, where Unlock day lives. Absent when the person cannot unlock a day.
  onOpenDay?: (date: string) => void
}

export function LateDataPanel({ user, onChanged, toast, canEdit, refreshKey = 0, onOpenConflict, onOpenDay }: PanelProps) {
  const [state, setState] = useState<'open' | 'resolved'>('open')
  const [items, setItems] = useState<LateItem[] | null>(null)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<LateItem | null>(null)
  const [all, setAll] = useState(false)
  const seq = useRef(0)

  const load = useCallback(async () => {
    const n = ++seq.current
    try {
      const r = await apiJson<{ items: LateItem[] }>(user, `/api/product-activity/late-data?state=${state}`)
      if (n === seq.current) {
        setItems(r.items)
        setError('')
      }
    } catch (e: any) {
      if (n === seq.current) setError(e?.message || 'Could not load late data')
    }
  }, [user, state])
  useEffect(() => {
    setItems(null)
    load()
  }, [load, refreshKey])

  const decided = (message: string) => {
    toast(message)
    setShown(null)
    setAll(false)
    load()
    onChanged()
  }

  const actionable = (items || []).filter((x) => x.actionable && !x.blocked && x.bulk !== false)

  return (
    <div data-testid="late-data-panel">
      <div className={styles.lateBar}>
        <div className={styles.tabTrack} role="group" aria-label="Late data view">
          <button type="button" className={state === 'open' ? styles.tabOn : styles.tab} aria-pressed={state === 'open'} onClick={() => setState('open')}>
            Open
          </button>
          <button type="button" className={state === 'resolved' ? styles.tabOn : styles.tab} aria-pressed={state === 'resolved'} onClick={() => setState('resolved')}>
            Resolved
          </button>
        </div>
        <span className={styles.lateNote}>Not filtered by the period above: late data is shown whatever its day.</span>
        {state === 'open' && canEdit && actionable.length > 1 && (
          <button type="button" className={styles.ghostBtn} onClick={() => setAll(true)} data-testid="late-apply-all">
            Review and apply all ({actionable.length})
          </button>
        )}
      </div>
      {error && (
        <div className={styles.errorBar} role="alert">
          <span>{error}</span>
        </div>
      )}
      {items === null && !error ? (
        <div className={styles.empty}>Loading late data…</div>
      ) : items && !items.length ? (
        <div className={styles.empty} data-testid="late-empty">
          {state === 'open' ? 'Nothing is waiting. Anything that arrives after a day is closed will be listed here.' : 'Nothing has been decided yet.'}
        </div>
      ) : (
        <ul className={styles.lateList} aria-label={state === 'open' ? 'Open late data' : 'Resolved late data'}>
          {(items || []).map((it) => (
            <li key={`${it.kind}:${it.id}`} className={styles.lateRow} data-testid="late-row" data-kind={it.kind}>
              <div className={styles.lateMain}>
                <div className={styles.lateTitleRow}>
                  <span className={styles.lateKind}>{KIND_LABEL[it.kind]}</span>
                  <b className={styles.lateTitle} id={`late-title-${it.kind}-${it.id}`}>{it.title}</b>
                  {it.item_code && <span className={styles.lateItem}>{itemName(it)}</span>}
                </div>
                <div className={styles.lateSummary}>{it.summary}</div>
                <div className={styles.lateMeta}>
                  {state === 'open' ? (
                    <>
                      {it.worker && <span>{it.worker}</span>}
                      {it.work_date && <span>{dayLabel(it.work_date)}</span>}
                      <span>captured {stamp(it.captured_at)}</span>
                      <span>arrived {stamp(it.arrived_at)}</span>
                      {(it.kind !== 'weighing_conflict' || it.day_state) && (
                        <span className={it.day_state === 'LOCKED' ? styles.lateLocked : undefined}>{dayTag(it.day_state)}</span>
                      )}
                    </>
                  ) : (
                    <span>{stamp(it.decided_at)}</span>
                  )}
                </div>
                {state === 'open' && it.note && <div className={styles.lateWarn}>{it.note}</div>}
                {state === 'open' && it.blocked && <div className={styles.lateWarn}>{it.blocked.message}</div>}
                {state === 'open' && it.kind === 'weighing_conflict' && (
                  <div className={styles.lateInfo}>
                    Nothing was applied: the weights the bins carry stand. {it.day_state === 'LOCKED' ? 'A day it reaches is locked, so using it needs that day unlocked first. ' : ''}It is decided on the Batches screen{onOpenConflict ? '' : ', which needs access to Batches'}.
                  </div>
                )}
              </div>
              {state === 'open' && it.kind === 'weighing_conflict' && onOpenConflict && (
                <button type="button" className={styles.ghostBtn} onClick={() => onOpenConflict(it.id)} aria-describedby={`late-title-${it.kind}-${it.id}`} data-testid="late-open-conflict">
                  Open in Batches
                </button>
              )}
              {state === 'open' && it.kind !== 'weighing_conflict' && (
                <button type="button" className={styles.ghostBtn} onClick={() => setShown(it)} aria-describedby={`late-title-${it.kind}-${it.id}`}>
                  {!canEdit ? 'View' : it.actionable ? 'Review' : 'Acknowledge'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {shown && <LateDrawer user={user} canEdit={canEdit} item={shown} onOpenDay={onOpenDay} onClose={() => setShown(null)} onDecided={decided} />}
      {all && <ApplyAll user={user} onClose={() => setAll(false)} onDecided={decided} />}
    </div>
  )
}

function messageOf(e: any, fallback: string): string {
  return e?.body?.code === 'DAY_LOCKED' ? LOCKED_TEXT : e?.message || fallback
}

function LateDrawer({ user, canEdit, item, onOpenDay, onClose, onDecided }: { user: PanelProps['user']; canEdit: boolean; item: LateItem; onOpenDay?: (date: string) => void; onClose: () => void; onDecided: (m: string) => void }) {
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loadErr, setLoadErr] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState<'apply' | 'dismiss' | null>(null)
  const [err, setErr] = useState('')
  const el = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { busy: !!busy, dirty: !!reason.trim() && !busy, el, modal: true })
  const base = `/api/product-activity/late-data/${item.kind}/${item.id}`

  const loadPreview = useCallback(async () => {
    setPreview(null)
    setLoadErr('')
    try {
      setPreview(await apiJson<Preview>(user, `${base}/preview`, { body: {} }))
    } catch (e: any) {
      setLoadErr(messageOf(e, 'Could not preview this item'))
    }
  }, [user, base])
  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const why = reason.trim()
  const informational = item.kind === 'arrived_after_close' || item.kind === 'other_site_tag'
  const review = item.kind === 'qc_review'
  const clock = item.kind === 'crew_clock'
  const blocked = preview?.blocked || null
  const canApply = canEdit && !!preview?.preview_hash && !blocked && !informational && !!why && !busy && !preview.resolved
  const canDismiss = canEdit && !!why && !busy && !preview?.resolved

  const apply = async () => {
    if (!canApply || !preview?.preview_hash) return
    setBusy('apply')
    setErr('')
    try {
      const r = await apiJson<{ already_resolved?: boolean; message?: string; cycles?: unknown[] }>(user, `${base}/apply`, { body: { preview_hash: preview.preview_hash, reason: why } })
      onDecided(r.already_resolved ? r.message || 'Already applied' : review ? 'Scan accepted' : clock ? 'Put on the timesheet' : 'Late data applied')
    } catch (e: any) {
      setErr(messageOf(e, 'It was not applied'))
      // The ground moved: show what applying it would do now.
      if (e?.body?.code === 'STALE_PREVIEW') loadPreview()
    } finally {
      setBusy(null)
    }
  }
  const dismiss = async () => {
    if (!canDismiss) return
    setBusy('dismiss')
    setErr('')
    try {
      const r = await apiJson<{ already_resolved?: boolean; message?: string }>(user, `${base}/dismiss`, { body: { reason: why } })
      onDecided(r.already_resolved ? r.message || 'Already decided' : informational ? 'Acknowledged' : 'Dismissed')
    } catch (e: any) {
      setErr(messageOf(e, 'It was not saved'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={styles.backdrop} onClick={requestClose}>
      <div ref={el} className={`${styles.drawer} ${pv.drawerPanel}`} role="dialog" aria-label="Late data" onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHead}>
          <div className={styles.drawerTitleRow}>
            <div className={styles.drawerTitleCol}>
              <div className={styles.drawerTitle}>{item.title}</div>
              <div className={styles.drawerSub}>
                {KIND_LABEL[item.kind]}
                {item.item_code ? ` · ${itemName(item)}` : ''}
                {item.work_date ? ` · ${dayLabel(item.work_date)}` : ''}
              </div>
            </div>
            <div className={styles.drawerChips}>
              <span className={styles.dayChipSmall}>{dayTag(item.day_state)}</span>
              <button type="button" className={`${o.close} ${pv.drawerClose}`} onClick={requestClose} title="Close" aria-label="Close" disabled={!!busy}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className={styles.lateSummary}>{item.summary}</div>
        </div>
        <div className={styles.drawerBody} data-testid="late-preview">
          {loadErr && (
            <div className={styles.errorBar} role="alert">
              <span>{loadErr}</span>
            </div>
          )}
          {!preview && !loadErr && <div className={styles.empty}>Working out what this would change…</div>}
          {preview?.resolved && <div className={styles.lateWarn}>{preview.message}</div>}
          {review && (item.review_hold || preview?.review) && !preview?.resolved && (
            <div className={styles.lateInfo} data-testid="late-hold">
              <b>Why it is held.</b> {HOLD_TEXT[item.review_hold || preview?.review?.review_hold || ''] || preview?.review?.reason || item.summary} Nothing was
              applied. Accepting it reads it as any other scan from now on, and the reason it was held stays on its record.
            </div>
          )}
          {informational && preview && (
            <div className={styles.lateInfo}>
              {item.summary}. It was made as a normal cycle and nothing needs applying. {item.note ? item.note + '. ' : ''}Acknowledge it once you have seen it.
            </div>
          )}
          {blocked && (
            <div className={styles.lateBlocked} role="alert" data-testid="late-blocked">
              <b>{blocked.code === 'DAY_LOCKED' ? LOCKED_TEXT : blocked.message}</b>
              {blocked.locked_dates.length > 0 && <span> Locked: {blocked.locked_dates.map(dayLabel).join(', ')}.</span>}
              {blocked.code === 'DAY_LOCKED' && <span> Unlocking recalculates that whole day. You can still dismiss this item with a reason.</span>}
              {blocked.code === 'DAY_LOCKED' && onOpenDay && blocked.locked_dates.length > 0 && (
                <span className={styles.lateUnlock}>
                  {blocked.locked_dates.map((d) => (
                    <button key={d} type="button" className={styles.ghostBtn} onClick={() => onOpenDay(d)} data-testid="late-open-day">
                      Unlock {dayLabel(d)} on Timesheets
                    </button>
                  ))}
                </span>
              )}
            </div>
          )}
          {preview && !informational && !blocked && !preview.resolved && (
            <>
              <div className={styles.lateH}>{review ? 'If you accept this' : 'If you apply this'}</div>
              {clock && preview.clock && (
                <div className={styles.lateInfo} data-testid="late-clock">
                  {preview.clock.text} The timesheet records that it was applied from late data, and wages are recalculated for the day.
                </div>
              )}
              {!clock && !preview.cycles?.length && <div className={styles.lateInfo}>It would change no cycle.</div>}
              {(preview.cycles || []).map((c) => (
                <div key={c.id} className={styles.lateCycle} data-testid="late-cycle">
                  <div className={styles.lateCycleHead}>
                    <b>{itemName(c)}</b>
                    {c.is_new && <span className={styles.lateTag}>New cycle</span>}
                    {c.worker_change && (
                      <span className={styles.lateTag}>
                        Worker {c.worker_change.before} → {c.worker_change.after}
                      </span>
                    )}
                  </div>
                  {c.changes.length > 0 && (
                    <table className={styles.lateTable}>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Before</th>
                          <th>After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.changes.map((x) => (
                          <tr key={x.field}>
                            <td>{x.label}</td>
                            <td>{x.before}</td>
                            <td>
                              <b>{x.after}</b>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {c.history_added.map((t, i) => (
                    <div key={i} className={styles.lateHist}>
                      {t}
                    </div>
                  ))}
                </div>
              ))}
              {!!preview.wage_targets?.length && (
                <>
                  <div className={styles.lateH}>Wages</div>
                  <ul className={styles.lateWages} data-testid="late-wages">
                    {preview.wage_targets.map((w) => (
                      <li key={`${w.human_id}|${w.work_date}`}>
                        <b>{w.worker}</b> · {dayLabel(w.work_date)} · {w.queued ? 'recalculation queued' : 'not recalculated until the day is unlocked'}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {canEdit && !preview?.resolved && (
            <label className={styles.formFieldWide}>
              <span className={styles.label}>Reason (required, kept with the decision)</span>
              <input
                className={styles.pillInput}
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder={informational ? 'Seen' : review ? 'Why you are accepting or dismissing this scan' : 'Why you are applying or dismissing this'}
                aria-label="Reason"
              />
            </label>
          )}
          {err && (
            <div className={styles.errorBar} role="alert" data-testid="late-error">
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className={pv.foot}>
          {canEdit && !informational && (
            <PendingButton busy={busy === 'apply'} pending="Applying…" className={styles.primaryBtn} onClick={apply} disabled={!canApply}>
              {review ? 'Accept' : 'Apply'}
            </PendingButton>
          )}
          {canEdit && <PendingButton busy={busy === 'dismiss'} pending="Saving…" className={styles.ghostBtn} onClick={dismiss} disabled={!canDismiss}>
            {informational ? 'Acknowledge' : 'Dismiss'}
          </PendingButton>}
          <button type="button" className={styles.ghostBtn} onClick={requestClose} disabled={!!busy}>
            Close
          </button>
        </div>
      </div>
      {confirm}
    </div>
  )
}

interface AllRow {
  kind: LateKind
  id: string
  title: string
  item_code: string | null
  tagless?: boolean
  preview_hash: string | null
  blocked: { code: string; message: string; locked_dates: string[] } | null
  cycles_touched: number
  wage_targets: number
}

function ApplyAll({ user, onClose, onDecided }: { user: PanelProps['user']; onClose: () => void; onDecided: (m: string) => void }) {
  const [rows, setRows] = useState<AllRow[] | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const el = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { busy, dirty: !!reason.trim() && !busy, el, modal: true, focusFirst: true })
  useEffect(() => {
    let live = true
    apiJson<{ items: AllRow[] }>(user, '/api/product-activity/late-data/preview-all', { body: {} })
      .then((r) => live && setRows(r.items))
      .catch((e) => live && setErr(e?.message || 'Could not preview the items'))
    return () => {
      live = false
    }
  }, [user])
  const ready = (rows || []).filter((r) => !r.blocked && r.preview_hash)
  const apply = async () => {
    if (busy || !reason.trim() || !ready.length) return
    setBusy(true)
    setErr('')
    try {
      const r = await apiJson<{ applied: number; refused: number }>(user, '/api/product-activity/late-data/apply-all', {
        body: { reason: reason.trim(), items: ready.map((x) => ({ kind: x.kind, id: x.id, preview_hash: x.preview_hash })) },
      })
      onDecided(`${r.applied} applied` + (r.refused ? ` · ${r.refused} left open (changed since the preview, or locked)` : ''))
    } catch (e: any) {
      setErr(messageOf(e, 'Nothing was applied'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={el} tabIndex={-1} className={o.modal} role="dialog" aria-label="Apply all late data">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Apply all</h2>
            <p className={o.sub}>Each item is applied on its own, with the preview you see here. One that changed meanwhile, or sits on a locked day, is left open.</p>
          </div>
          <button type="button" className={o.close} onClick={requestClose} aria-label="Close" disabled={busy}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          {!rows && !err && <div className={o.note}>Previewing…</div>}
          {rows && (
            <ul className={pv.blockedList} aria-label="Items to apply" data-testid="late-all-list">
              {rows.map((r) => (
                <li key={`${r.kind}:${r.id}`}>
                  <b>{r.title}</b>
                  <span>{itemName(r)}</span>
                  <em>{r.blocked ? (r.blocked.code === 'DAY_LOCKED' ? LOCKED_TEXT + ' Left open.' : r.blocked.message) : `${r.cycles_touched} cycle${r.cycles_touched === 1 ? '' : 's'} · ${r.wage_targets} wage day${r.wage_targets === 1 ? '' : 's'}`}</em>
                </li>
              ))}
            </ul>
          )}
          <label className={o.field}>
            <span className={o.fieldLabel}>
              Reason <span className={o.req}>required, kept with every decision</span>
            </span>
            <input className={o.input} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
          </label>
          {err && (
            <div className={o.danger} role="alert">
              {err}
            </div>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <PendingButton busy={busy} pending="Applying…" className={o.primary} onClick={apply} disabled={!reason.trim() || !ready.length}>
            Apply {ready.length || ''}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
